// @flow

import * as React from 'react';

type Props = {|
  /** A number to specify a pixel-count fuzz-factor for determining visibility */
  +threshold: number,

  /** The rootMargin per Intersection Observer API. Grows/shrinks each side of the element's bounding box before computing intersections. */
  +rootMargin: string,

  /** A callback to be called when the ref'd element becomes visible */
  +onVisible: ?() => void,

  /** A callback to be called when the ref'd element becomes invisible */
  +onInvisible: ?() => void,

  /**
   * Render prop providing a target function (which should be attached to the element you are
   * interested in the visibility of as a ref), and the current visibility as a bool
   *
   * Alternatively, if you're passing a node prop, you can just pass normal React children.
   */
  +children: React.Node | ((React.Ref<*>, boolean) => React.Node),

  /**
   * Instead of setting the ref via the render prop API, you can pass the raw element in directly.
   */
  +node: null | { current: HTMLElement | null },
|};

type State = {|
  visible: boolean,
|};

/**
 * An efficient implementation of tracking when a DOM element becomes visible or invisible.
 *
 * This uses IntersectionObserver rather than scroll listeners for better performance
 * on modern browsers.
 *
 * @author skline
 * @since 2018-10-09
 */
class VisibilityObserver extends React.PureComponent<Props, State> {
  static defaultProps = {
    threshold: 0,
    rootMargin: '0px',
    node: null,
    onVisible: null,
    onInvisible: null,
  };

  constructor(props: Props) {
    super(props);
    this.elmt = this.props.node ? this.props.node : React.createRef();
    this.state = { visible: false };
    this.observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(
            entries => {
              const isVisible = entries.reduce(
                (viewed, entry) => viewed || entry.isIntersecting,
                false,
              );
              if (isVisible && !this.state.visible) {
                this.setState({ visible: true });
                if (this.props.onVisible) {
                  this.props.onVisible();
                }
              } else if (!isVisible && this.state.visible) {
                this.setState({ visible: false });
                if (this.props.onInvisible) {
                  this.props.onInvisible();
                }
              }
            },
            {
              rootMargin: this.props.rootMargin,
              threshold: this.props.threshold,
            },
          );
  }

  componentDidMount() {
    this.watch();
  }

  componentDidUpdate() {
    this.watch();
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  watch() {
    if (!this.observer) {
      return;
    }
    this.observer.disconnect();
    if (!this.observer) return; // For Flow
    if (this.elmt.current) {
      this.observer.observe(this.elmt.current);
    } else if (this.props.node && this.props.node.current) {
      this.observer.observe(this.props.node.current);
    }
  }

  elmt: React.ElementRef<*>;
  observer: ?IntersectionObserver;

  render() {
    if (typeof this.props.children !== 'function') {
      return this.props.children;
    }
    return this.props.children(this.elmt, this.state.visible);
  }
}

export default VisibilityObserver;
